/**
 * AICommandBar
 *
 * Natural language command input for AI structural editing.
 * Expands from a sparkle icon button into a text input.
 * Sends commands to the AI backend and previews changes before applying.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { sendAICommand, applyStructuralChanges } from '../../api/ai-editor';
import type { AICommandResponse, ChangePreview } from '../../api/ai-editor';

// =============================================================================
// TYPES
// =============================================================================

interface AICommandBarProps {
  websiteId: string;
  currentPage: string;
  selectedSection?: string | null;
  onContentChange: () => void;
}

type CommandState = 'idle' | 'loading' | 'preview' | 'applying' | 'success' | 'error' | 'clarification';

// =============================================================================
// COMPONENT
// =============================================================================

export function AICommandBar({
  websiteId,
  currentPage,
  selectedSection,
  onContentChange,
}: AICommandBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [command, setCommand] = useState('');
  const [state, setState] = useState<CommandState>('idle');
  const [preview, setPreview] = useState<ChangePreview | null>(null);
  const [message, setMessage] = useState('');
  const [clarificationPrompt, setClarificationPrompt] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const dismissRef = useRef<ReturnType<typeof setTimeout>>();

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isExpanded]);

  // Auto-dismiss success/error after 3s
  useEffect(() => {
    if (state === 'success' || state === 'error') {
      dismissRef.current = setTimeout(() => {
        setState('idle');
        setMessage('');
        if (state === 'success') {
          setCommand('');
          setIsExpanded(false);
        }
      }, 3000);
      return () => {
        if (dismissRef.current) clearTimeout(dismissRef.current);
      };
    }
  }, [state]);

  const handleSubmit = useCallback(async () => {
    if (!command.trim() || state === 'loading' || state === 'applying') return;

    setState('loading');
    setMessage('');
    setPreview(null);

    try {
      const result: AICommandResponse = await sendAICommand(websiteId, command.trim(), {
        currentPage,
        selectedSection: selectedSection || undefined,
      });

      if (result.clarification_needed && result.clarification_prompt) {
        setState('clarification');
        setClarificationPrompt(result.clarification_prompt);
        return;
      }

      if (!result.success || !result.preview) {
        setState('error');
        setMessage(result.error || 'Could not understand the command');
        return;
      }

      setState('preview');
      setPreview(result.preview);
    } catch (err) {
      setState('error');
      setMessage(err instanceof Error ? err.message : 'Failed to process command');
    }
  }, [command, state, websiteId, currentPage, selectedSection]);

  const handleApply = useCallback(async () => {
    if (!preview?.changes || state === 'applying') return;

    setState('applying');
    try {
      const result = await applyStructuralChanges(websiteId, preview.changes);
      if (result.success) {
        setState('success');
        setMessage(preview.description || 'Changes applied');
        setPreview(null);
        onContentChange();
      } else {
        setState('error');
        setMessage(result.error || 'Failed to apply changes');
      }
    } catch (err) {
      setState('error');
      setMessage(err instanceof Error ? err.message : 'Failed to apply changes');
    }
  }, [preview, state, websiteId, onContentChange]);

  const handleCancel = useCallback(() => {
    setState('idle');
    setPreview(null);
    setMessage('');
    setClarificationPrompt('');
  }, []);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setCommand('');
    setState('idle');
    setPreview(null);
    setMessage('');
    setClarificationPrompt('');
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (state === 'preview') {
          handleApply();
        } else if (state === 'idle' || state === 'error' || state === 'clarification') {
          handleSubmit();
        }
      }
      if (e.key === 'Escape') {
        if (state === 'preview' || state === 'clarification') {
          handleCancel();
        } else {
          handleClose();
        }
      }
    },
    [state, handleSubmit, handleApply, handleCancel, handleClose]
  );

  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        title="AI Command (natural language editing)"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
        <span className="hidden sm:inline">AI</span>
      </button>
    );
  }

  return (
    <div className="relative flex items-center gap-2">
      {/* Input */}
      <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400 transition-all">
        <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g., Add a testimonials section..."
          disabled={state === 'loading' || state === 'applying'}
          className="w-48 sm:w-64 text-sm text-gray-900 bg-transparent border-none outline-none placeholder-gray-400 disabled:opacity-50"
        />
        {(state === 'loading' || state === 'applying') && (
          <svg className="w-4 h-4 text-blue-500 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
        <button
          onClick={handleClose}
          className="p-0.5 text-gray-400 hover:text-gray-600 shrink-0"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Preview / Clarification / Error Dropdown */}
      {(state === 'preview' || state === 'clarification' || state === 'success' || state === 'error') && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-30 overflow-hidden">
          {state === 'preview' && preview && (
            <div className="p-3">
              <p className="text-sm text-gray-700 mb-2">{preview.description}</p>
              <div className="flex gap-2">
                <button
                  onClick={handleCancel}
                  className="flex-1 px-2 py-1.5 text-xs text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApply}
                  className="flex-1 px-2 py-1.5 text-xs text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
          {state === 'clarification' && (
            <div className="p-3">
              <p className="text-sm text-gray-700 mb-1">{clarificationPrompt}</p>
              <p className="text-xs text-gray-400">Update your command and try again.</p>
            </div>
          )}
          {state === 'success' && (
            <div className="p-3 bg-green-50 flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-sm text-green-700">{message}</p>
            </div>
          )}
          {state === 'error' && (
            <div className="p-3 bg-red-50 flex items-center gap-2">
              <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">{message}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
